import { useEffect, useMemo } from 'react';
import { sortBy } from 'lodash';

import {
  type AutocompleteValue,
  type ExtractOption,
  type OnChangeEvent,
  type OptionsInput,
  type UseAutocompleteOptionsArgs,
  type UseAutocompleteOptionsRes,
} from './autocomplete.types';

import useEvent from '../useEvent';

type BaseOptions<TOptions extends OptionsInput | undefined = undefined> = {
  data: TOptions;
  isLoading?: boolean;
  sort?: boolean | ((option: ExtractOption<TOptions>) => unknown); // if you pass a function, it should be memoized
};

type Options<
  TOptions extends OptionsInput,
  TMultiple extends boolean | undefined,
  TDisableClearable extends boolean | undefined,
> = BaseOptions<TOptions> & UseAutocompleteOptionsArgs<TOptions, TMultiple, TDisableClearable>;

export type Input = Options<OptionsInput, boolean, boolean>;

const useAutocompleteOptions = <
  TOptions extends OptionsInput,
  TMultiple extends boolean | undefined = undefined,
  TDisableClearable extends boolean | undefined = undefined,
>({
  callback,
  data,
  disableClearable,
  disableCloseOnSelect = false,
  disabled = false,
  enforceSingleOptionPrefill = true,
  flexGrow = false,
  isLoading = false,
  multiple,
  selected,
  sort = false,
}: Options<TOptions, TMultiple, TDisableClearable>): UseAutocompleteOptionsRes<
  TOptions,
  TMultiple
> => {
  type Value = AutocompleteValue<ExtractOption<TOptions>, TMultiple, TDisableClearable>;

  const callbackFn = useEvent(callback);

  const sortedData = useMemo(() => {
    if (!sort) return data ?? [];

    const sortFn = sort === true ? (option: ExtractOption<TOptions>) => option.label : sort;

    return sortBy(data, sortFn);
  }, [data, sort]);

  const value = useMemo(() => {
    if (!selected) return multiple ? [] : null;

    if (Array.isArray(selected)) {
      return data?.filter((option) => selected?.includes(option.value)) || [];
    }

    return data?.find((option) => option.value === selected) || null;
  }, [data, multiple, selected]);

  const handleChange = useEvent((_: OnChangeEvent, newValue: Value) => {
    const validMulti = !!multiple && Array.isArray(newValue);
    const validSingle = !multiple && (!disableClearable || !!newValue);

    if (!validMulti && !validSingle) return;

    callbackFn(newValue);
  });

  useEffect(() => {
    const hasValue = !!value && (!Array.isArray(value) || !!value.length);

    if (!enforceSingleOptionPrefill || hasValue || data?.length !== 1) return;

    const newValue = (multiple ? data : data[0]) as Value;

    callbackFn(newValue);
  }, [callbackFn, data, enforceSingleOptionPrefill, multiple, value]);

  // typing is messy for this hook, but not touching for now
  // seems like it would be easy to cause a bunch of type errors all over the place
  return {
    className: flexGrow ? 'min-w-0 flex-1' : undefined,
    disableClearable: !!disableClearable && value !== null,
    disableCloseOnSelect: multiple || disableCloseOnSelect,
    disabled: isLoading || (enforceSingleOptionPrefill && data?.length === 1) || disabled,
    getOptionKey: (option: ExtractOption<TOptions>) => option.value,
    loading: isLoading,
    multiple: (multiple ?? undefined) as TMultiple,
    onChange: handleChange,
    options: sortedData as ExtractOption<TOptions>[],
    value: value as Value,
  };
};

export default useAutocompleteOptions;
